import { useGetContentsByContentType, useGetRelatedContents } from "@/hooks";
import { CONTENT_TYPES } from "@/utils/content";
import { formatDate } from "@/utils/datetime";
import Image from "next/legacy/image";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import { fitIn } from "@/utils/imageHandler";
import { getContents } from "@/services/api";
import CarouselTrending from "@/components/parts/CarouselTrending";
import CarouselRelatedContent from "./parts/CarouselRelatedContent";
import { useIsMobile } from "@/hooks/useIsMobile";

const mapContentTypeNameWithSlug = {
  Videos: "videos",
  "Special Reports": "reports",
  Spotlights: "spotlights",
  Infobytes: "infobytes",
  Articles: "articles",
  News: "news",
  Opinions: "opinions",
  "Podcast Episodes": "podcasts-masterclasses",
};

const DEFAULT_AUTHOR_NAME = "Staff Writer";

const formatAuthorNames = (authors) => {
  if (!Array.isArray(authors)) {
    return "";
  }

  const names = authors.map((author) => author?.name).filter(Boolean);

  if (names.length === 0) {
    return "";
  }

  if (names.length === 1) {
    return names[0];
  }

  if (names.length === 2) {
    return names.join(" and ");
  }

  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

const getAuthorName = (content) => {
  const authorsName = formatAuthorNames(content?.authors);

  if (authorsName) {
    return authorsName;
  }

  if (typeof content?.author === "string") {
    return content.author;
  }

  return content?.author?.name || content?.author_name || DEFAULT_AUTHOR_NAME;
};

const RelatedContentByline = ({ content }) => {
  const authorName = getAuthorName(content);
  const publishDate = formatDate(content.publish_date, "MMMM DD, YYYY");

  return (
    <span
      className='block min-w-0 mt-1 text-xs md:mt-2 text-white/50'
      title={`By ${authorName} - ${publishDate}`}
    >
      <span className='block truncate'>By {authorName}</span>
      <span className='block mt-0.5'>{publishDate}</span>
    </span>
  );
};

const fetchRelatedContents = async (currentContentId) => {
  try {
    const response = await fetch(
      `https://api.conversease.com/lcdt/related/${currentContentId}`
    );
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to fetch additional related contents:", error);
    return [];
  }
};

const fetchSimilarContents = async (text) => {
  try {
    const response = await getContents({
      search: text,
      sort: "newest",
      per_page: 10,
      exact: 1,
    });

    return response.data.data;
  } catch (error) {
    console.error("Failed to fetch similar contents:", error);
    return [];
  }
};

const RelatedContentSidebar = ({
  title,
  currentContentId,
  contentType,
  detailPrefix,
  entity,
  id,
}) => {
  const isMobile = useIsMobile();
  const [relatedContents, isLoading] = useGetRelatedContents(
    contentType,
    currentContentId,
    contentType ? 3 : 10,
    false,
    entity
  );

  const [additionalContents, setAdditionalContents] = useState([]);

  useEffect(() => {
    if (!contentType) {
      if (currentContentId) {
        const getAdditionalContents = async () => {
          try {
            const data = await fetchRelatedContents(currentContentId);

            // Filter out any content that is already in relatedContents
            const filteredAdditionalContents = data?.related_contents?.filter(
              (content) =>
                !relatedContents.some(
                  (related) => related.uuid === content.uuid
                )
            );

            setAdditionalContents(filteredAdditionalContents);
          } catch (error) {
            setAdditionalContents([]);
          }
        };

        getAdditionalContents();
      } else if (entity && entity.name) {
        const fetchSimilar = async (text) => {
          try {
            const data = await fetchSimilarContents(text);

            // Filter out any content that is already in relatedContents
            const filteredAdditionalContents = data?.filter(
              (content) =>
                !relatedContents.some(
                  (related) => related.uuid === content.uuid
                )
            );

            setAdditionalContents(filteredAdditionalContents);
          } catch (error) {
            setAdditionalContents([]);
          }
        };
        fetchSimilar(entity.name);
      }
    }
  }, [currentContentId, relatedContents, contentType, id]);

  return (
    <>
      {relatedContents.length > 0 && (
        <div>
          <p className='text-lg font-semibold md:text-2xl lg:text-lg lg:pb-2 lg:mb-4 lg:border-b border-white/10'>
            {title}
          </p>
          {isMobile ? (
            <div className='-mx-4'>
              <CarouselRelatedContent data={relatedContents} />
            </div>
          ) : (
            <div className='flow-root'>
              <ul
                role='list'
                className='-my-4 divide-y divide-dashed divide-white/10'
              >
                {relatedContents.map((content) => (
                  <li className='py-4' key={`related-content-${content.uuid}`}>
                    <Link
                      href={`/${
                        detailPrefix
                          ? detailPrefix
                          : mapContentTypeNameWithSlug[
                              content.content_type.name
                            ]
                      }/${content.slug}`}
                      className='flex items-start space-x-3 group'
                    >
                      <span className='relative flex-shrink-0 overflow-hidden rounded-md size-12 lg:size-16 aspect-square'>
                        <Image
                          objectFit='cover'
                          layout='fill'
                          src={fitIn(
                            content.content_type.name
                              ? [
                                  CONTENT_TYPES.INFOBYTES,
                                  CONTENT_TYPES.PODCAST_EPISODES,
                                  CONTENT_TYPES.VIDEOS,
                                  CONTENT_TYPES.VIDEOS_AND_PODCAST_EPISODES,
                                ].includes(content.content_type.name)
                                ? content.thumbnail_url
                                : content.cover_image_url
                              : content.thumbnail_url,
                            600
                          )}
                          alt={content.image_alt || ""}
                        />
                      </span>
                      <span className='flex-1 min-w-0'>
                        <span className='text-sm font-medium leading-tight lg:-mt-1 line-clamp-2 group-hover:underline'>
                          {content.title}
                        </span>
                        <RelatedContentByline content={content} />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {!contentType && (
        <>
          {additionalContents?.length > 0 && (
            <div>
              <p className='text-lg font-semibold md:text-2xl lg:text-lg lg:pb-2 lg:mb-4 lg:border-b border-white/10'>
                Other Related Contents
              </p>
              {isMobile ? (
                <div className='-mx-4'>
                  <CarouselRelatedContent data={additionalContents} />
                </div>
              ) : (
                <div className='flow-root mt-5 lg:mt-0'>
                  <ul
                    role='list'
                    className='-my-4 divide-y divide-dashed divide-white/10'
                  >
                    {additionalContents.map((content) => (
                      <li
                        className='py-4'
                        key={`related-content-${content.uuid}`}
                      >
                        <Link
                          className='flex items-start gap-5 lg:gap-4 group'
                          href={`/${
                            detailPrefix
                              ? detailPrefix
                              : mapContentTypeNameWithSlug[
                                  content.content_type.name
                                ]
                          }/${content.slug}`}
                        >
                          <span className='relative flex-shrink-0 overflow-hidden rounded-md size-12 md:size-20 lg:size-16 aspect-square'>
                            <Image
                              objectFit='cover'
                              layout='fill'
                              src={fitIn(
                                content.content_type.name
                                  ? [
                                      CONTENT_TYPES.INFOBYTES,
                                      CONTENT_TYPES.PODCAST_EPISODES,
                                      CONTENT_TYPES.VIDEOS,
                                      CONTENT_TYPES.VIDEOS_AND_PODCAST_EPISODES,
                                    ].includes(content.content_type.name)
                                    ? content.thumbnail_url
                                    : content.cover_image_url
                                  : content.thumbnail_url,
                                600
                              )}
                              alt={content.image_alt || ""}
                            />
                          </span>
                          <span className='flex-1 min-w-0'>
                            <p className='mb-1 text-xs font-medium uppercase lg:mb-2 opacity-40'>
                              {content.content_type.name}
                            </p>
                            <p className='font-medium md:leading-tight md:text-lg lg:text-sm line-clamp-2 group-hover:underline'>
                              {content.title}
                            </p>
                            <RelatedContentByline content={content} />
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default RelatedContentSidebar;
